import type { AppearanceSettings } from 'ProdantixSdk/chat/appearance';
import type { ChatStorage } from 'ProdantixSdk/chat/client';
import type { Availability } from 'ProdantixSdk/chat/hours';
import type { TeamFace } from 'ProdantixSdk/chat/launcher';
import { TransportError } from 'ProdantixSdk/core/errors';
import { type FetchLike, resolveFetch, trimTrailingSlash } from 'ProdantixSdk/core/http';

export interface StarterPrompt {
  html: string;
  text: string;
}

export interface MessengerConfig extends AppearanceSettings {
  enabled: boolean;
  launcherPosition: 'bottom_left' | 'bottom_right';
  launcherOffsetX: number;
  launcherOffsetY: number;
  accentColor: string;
  greeting: string;
  awayMessage: string;
  availability: Availability;
  version: string;
  /// Resolved for one locale by the edge, at most three, and empty for a project
  /// that has configured none. They are what the first-run panel offers a
  /// visitor instead of an empty log.
  starterPrompts: StarterPrompt[];
  /// The people the project picked (user picks of 2026-09-13), in order, as
  /// the edge resolved them: a name, initials on the accent and a picture.
  /// The launcher draws the first of them; a turn names its author from the
  /// same rule.
  team: TeamFace[];
  /// Where to open the live connection. `prodantix-ws` is a different hostname
  /// from the edge, so this is told rather than derived. Empty means the
  /// deployment has no socket and the widget stays on its poll.
  socketUrl: string;
  attachments?: MessengerAttachments;
  help?: MessengerHelp | null;
  tickets?: MessengerTickets | null;
  /// The "Powered by Prodantix" line in the visitor's language, absent on a
  /// white-label plan, which draws nothing and leaves no gap.
  poweredBy?: string | null;
}

export interface HelpWords {
  messagesTab: string;
  helpTab: string;
  searchPlaceholder: string;
  collections: string;
  mostRead: string;
  mightHelp: string;
  noMatches: string;
  back: string;
  openOnSite: string;
  stillNeedHelp: string;
  messageUs: string;
  readOnSite: string;
  wasItHelpful: string;
  thanks: string;
  smiley: string;
  neutral: string;
  disappointed: string;
  rateArticle: string;
}

/// The help centre the widget searches, present only while the project has
/// article search on and its company has a help site connected.
export interface MessengerHelp {
  placement: 'first_view' | 'tab' | 'while_typing';
  words: HelpWords;
}

export interface TicketWords {
  messagesTab: string;
  ticketsTab: string;
  yourTickets: string;
  noTickets: string;
  openTicket: string;
  whatYouNeed: string;
  subject: string;
  choose: string;
  send: string;
  repliesHere: string;
  reply: string;
  back: string;
  failed: string;
}

/// Present while the project takes tickets from visitors: a customer ticket
/// type they may submit exists, so the widget offers its Tickets tab.
export interface MessengerTickets {
  words: TicketWords;
}

export interface MessengerAttachments {
  enabled: boolean;
  maxBytes: number;
  accept: string[];
}

export interface MessengerConfigOptions {
  apiKey: string;
  host: string;
  locale?: string;
  storage?: ChatStorage;
  fetchImpl?: FetchLike;
  onError?: (error: unknown) => void;
}

/// One key, one JSON value, carrying the project it belongs to. Two entries
/// under two keys risks one surviving without the other, and an entry with no
/// project recorded would be served to whichever key asked for it next.
export const MESSENGER_CACHE_KEY = 'prodantix.messenger.config';

/// Every language the visitor reads, in their own order, which is what the edge
/// walks. The page's own `lang` is the host's choice of what to publish, not the
/// reader's, so it is only the fallback for a browser that lists nothing.
export const localeCandidates = (win: Window): string | undefined => {
  const listed = win.navigator?.languages;
  if (Array.isArray(listed) && listed.length > 0) return listed.join(',');
  return win.navigator?.language || win.document?.documentElement?.lang || undefined;
};

export const cacheKeyFor = (apiKey: string, locale: string | undefined): string => `${MESSENGER_CACHE_KEY}:${apiKey}:${locale ?? ''}`;

interface CacheEntry {
  apiKey: string;
  config: MessengerConfig;
}

export function cachedMessengerConfig(storage: ChatStorage | undefined, apiKey: string, locale?: string): MessengerConfig | null {
  const raw = storage?.get(cacheKeyFor(apiKey, locale));
  if (!raw) {
    return null;
  }
  try {
    const entry = JSON.parse(raw) as CacheEntry;
    return entry?.apiKey === apiKey ? entry.config : null;
  } catch {
    // A corrupt entry is not worth throwing into a customer's page. Treat it as
    // a miss; the next successful fetch overwrites it.
    return null;
  }
}

export async function fetchMessengerConfig(options: MessengerConfigOptions): Promise<MessengerConfig | null> {
  const fetchImpl = resolveFetch(options.fetchImpl);
  if (!fetchImpl) {
    options.onError?.(new TransportError('no fetch available for the prodantix messenger'));
    return null;
  }
  const host = trimTrailingSlash(options.host);
  const cacheKey = cacheKeyFor(options.apiKey, options.locale);
  const query = options.locale ? `?locale=${encodeURIComponent(options.locale)}` : '';
  try {
    const response = await fetchImpl(`${host}/v1/messenger/config${query}`, {
      headers: { authorization: `Bearer ${options.apiKey}` },
      method: 'GET',
    });
    if (!response.ok) {
      options.onError?.(new TransportError(`prodantix messenger config: ${response.status}`, response.status));
      return null;
    }
    const config = JSON.parse(await response.text()) as MessengerConfig;
    if (!config.enabled) {
      // Switching it off must take effect on the next load. Leaving the previous
      // entry would keep every repeat visitor painting a launcher the customer
      // had already turned off.
      options.storage?.remove(cacheKey);
      return config;
    }
    options.storage?.set(cacheKey, JSON.stringify({ apiKey: options.apiKey, config } satisfies CacheEntry));
    return config;
  } catch (error) {
    // Covers both a transport rejection and a body that will not parse. A proxy
    // answering 200 with an HTML error page is the second case, and it must not
    // throw into the host page.
    options.onError?.(error);
    return null;
  }
}
